import { ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { ImageLoader, MessageDisplay } from 'components/common';
import { ProductShowcaseGrid } from 'components/product';
import { SHOP, SIGNUP } from 'constants/routes';
import {
  displayMoney,
  displayDate,
  displayAddress,
  displayActionMessage
} from 'helpers/utils';
import {
  useDocumentTitle,
  useLatestProducts,
  useProduct,
  useModal,
  useScrollTop
} from 'hooks';
import firebase from 'firebase/firebase';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MessageModal from './MessageModal';
import defaultAvatar from '../../images/defaultAvatar.png';

const ViewProduct = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { product, isLoading, error } = useProduct(id);
  useScrollTop();
  useDocumentTitle(`${product?.name || 'Item'} | Uilpi`);

  const store = useSelector((state) => ({
    user: state.auth
  }));
  const { profile } = useSelector((state) => ({
    profile: state.profile
  }));

  const [selectedImage, setSelectedImage] = useState(product?.image || '');

  const onClickLink = (e) => {
    if (store.isAuthenticating) e.preventDefault();
  };

  const modal = useModal();

  const openModal = () => {
    modal.onOpenModal();
  };

  const send = (message, to, from) => {
    if (message.length > 0) {
      firebase
        .createChat({
          message,
          to,
          from,
          name: profile.fullname,
          avatar: profile.avatar || defaultAvatar,
          timestamp: new Date().getTime()
        })
        .then(() => {
          displayActionMessage('confirmMessageSent', 'success');
          modal.onCloseModal();
        })
        .catch(() => displayActionMessage('errorMessageSent', 'error'));
    }
  };

  const onCreateChat = (message) => {
    if (message) {
      send(message, product.userId, store.user.id);
    }
  };

  const { latestProducts, fetchLatestProducts, isLoadingLatest, errorLatest } =
    useLatestProducts(4);

  useEffect(() => {
    setSelectedImage(product?.image);
  }, [product]);

  return (
    <main className="content">
      {isLoading && (
        <div className="loader">
          <h4>{t('loadingMessage')}</h4>
          <br />
          <LoadingOutlined style={{ fontSize: '3rem' }} />
        </div>
      )}
      {error && <MessageDisplay message={error} />}
      {product && !isLoading && (
        <div className="product-view">
          <Link to={SHOP}>
            <h3 className="button-link d-inline-flex">
              <ArrowLeftOutlined />
              &nbsp; {t('backShopButton')}
            </h3>
          </Link>
          <div className="product-modal">
            {product.imageCollection.length !== 0 && (
              <div className="product-modal-image-collection">
                {product.imageCollection.map((image) => (
                  <div
                    className="product-modal-image-collection-wrapper"
                    key={image.id}
                    onClick={() => setSelectedImage(image.url)}
                    role="presentation"
                  >
                    <ImageLoader
                      className="product-modal-image-collection-img"
                      src={image.url}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="product-modal-image-wrapper">
              <ImageLoader
                alt={product.name}
                className="product-modal-image"
                src={selectedImage}
              />
            </div>
            <div className="product-modal-details">
              <br />
              <span className="text-subtle">
                {displayDate(product.dateAdded)}
              </span>
              <h1 className="margin-top-0">{product.name}</h1>
              <h3>{displayAddress(product)}</h3>
              <div className="divider" />
              {product.category.type === 'Bicicletta' && (
                <>
                  <span>
                    {t('productFormBrand')}:{' '}
                    {product.category.brand === 'Non Specificata'
                      ? t('productFormNoBrand')
                      : product.category.brand}
                  </span>
                  <br />
                  <span>
                    {t('productFormType')}:{' '}
                    {product.category.sub1 === 'Strada'
                      ? t('productFormStreet')
                      : product.category.sub1}
                  </span>
                  <br />
                  <span>
                    {t('productFormPedaling')}:{' '}
                    {product.category.assisted === 'Assistita'
                      ? t('productFormAssisted')
                      : t('productFormNotAssisted')}
                  </span>
                  <br />
                  <span>
                    {t('productFormYear')}: {product.category.year}
                  </span>
                  <br />
                  <span>
                    {t('productFormSize')}: {product.category.size}
                  </span>
                  <br />
                  <span>
                    {t('productFormWeight')}: {product.category.weight} Kg
                  </span>
                  <br />
                  <span>
                    {t('productFormWheels')}: {product.category.wheels}&#34;
                  </span>
                  <br />
                </>
              )}
              <span>
                {t('productFormDescription')}: {product.description}
              </span>
              <div className="divider" />
              <br />
              <h1>{displayMoney(product)}</h1>
              <br />
              {!store.user
                ? [
                    product.sold || !product.visible ? (
                      <span key="0">{t('productNotAvailable')}</span>
                    ) : (
                      <div key="1">
                        <span>{t('productNotLoggedUser')}</span>
                        <br />
                        <br />
                        <Link
                          className="button button-small"
                          onClick={onClickLink}
                          to={SIGNUP}
                        >
                          {t('menuSignUp')}
                        </Link>
                      </div>
                    )
                  ]
                : [
                    product.userId === store.user.id ? (
                      <span key="2">{t('productOwnUser')}</span>
                    ) : (
                      [
                        product.sold || !product.visible ? (
                          <span key="3">{t('productNotAvailable')}</span>
                        ) : (
                          <div key="1">
                            <span>{t('productContactSeller')}</span>
                            <br />
                            <br />
                            <button
                              className="button button-small"
                              type="button"
                              onClick={openModal}
                            >
                              {t('productSendMessage')}
                            </button>
                            <MessageModal
                              onCreateChat={onCreateChat}
                              modal={modal}
                            />
                          </div>
                        )
                      ]
                    )
                  ]}
            </div>
          </div>
          <div style={{ marginTop: '10rem' }}>
            <div className="display-header">
              <h1>{t('lastAddedMessage')}</h1>
              <Link to={SHOP}>{t('lastAddedButton')}</Link>
            </div>
            {errorLatest && !isLoadingLatest ? (
              <MessageDisplay
                message={errorLatest}
                action={fetchLatestProducts}
                buttonLabel={t('errorButtonRetry')}
              />
            ) : (
              <ProductShowcaseGrid
                products={latestProducts}
                skeletonCount={4}
              />
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default ViewProduct;
